/**
 * shared/feature-flags.ts
 *
 * Single source of truth for AIP feature flags. Both the Express server and
 * the Vite-compiled client import from here, so flag names and defaults stay
 * in sync.
 *
 * Server reads from process.env.X.
 * Client reads from import.meta.env.VITE_X (Vite inlines this at build time).
 *
 * Each flag exports both:
 *   - a boolean getter (isFooEnabled())
 *   - the raw env var name (FOO_ENV_NAME) so callers can log it cleanly
 */

// ── env access helpers ──────────────────────────────────────────────────────

function readServerEnv(name: string): string | undefined {
  if (typeof process !== "undefined" && process.env) {
    return process.env[name];
  }
  return undefined;
}

function readClientEnv(name: string): string | undefined {
  // Vite exposes env vars on import.meta.env. We probe defensively so this
  // file also imports cleanly in plain Node test runners.
  try {
    const m = (import.meta as any)?.env;
    return m ? (m[name] as string | undefined) : undefined;
  } catch {
    return undefined;
  }
}

function truthy(v: string | undefined): boolean {
  if (!v) return false;
  return v === "true" || v === "1" || v === "yes";
}

// ── FEATURE_GMAIL_OAUTH ─────────────────────────────────────────────────────

export const FEATURE_GMAIL_OAUTH_SERVER_ENV = "FEATURE_GMAIL_OAUTH";
export const FEATURE_GMAIL_OAUTH_CLIENT_ENV = "VITE_FEATURE_GMAIL_OAUTH";

/**
 * Server-side check. Reads process.env.FEATURE_GMAIL_OAUTH.
 * Default: false. Any value other than "true"/"1"/"yes" is treated as off.
 */
export function isGmailOAuthEnabledServer(): boolean {
  return truthy(readServerEnv(FEATURE_GMAIL_OAUTH_SERVER_ENV));
}

/**
 * Client-side check. Reads import.meta.env.VITE_FEATURE_GMAIL_OAUTH.
 * Default: false. Inlined by Vite at build time, so flipping the flag
 * requires a redeploy — same behavior as the Outlook flag.
 */
export function isGmailOAuthEnabledClient(): boolean {
  return truthy(readClientEnv(FEATURE_GMAIL_OAUTH_CLIENT_ENV));
}

/**
 * Convenience that picks the right reader based on runtime.
 * Browsers have window; Node does not. We default to the server check
 * outside the browser so isomorphic call sites stay safe.
 */
export function isGmailOAuthEnabled(): boolean {
  if (typeof window !== "undefined") {
    return isGmailOAuthEnabledClient();
  }
  return isGmailOAuthEnabledServer();
}

/**
 * Stable user-facing error string used by every Google route when the flag
 * is off. Mirrored on the client so the IntegrationsPanel can show the
 * same wording when the user has somehow forced the call.
 */
export const GMAIL_OAUTH_DISABLED_ERROR =
  "Gmail/Google Calendar integration is not enabled. " +
  "Set FEATURE_GMAIL_OAUTH=true to enable.";
